import {
  BLACK_BIT,
  FLAG_CAPTURE,
  FLAG_DROP,
  KING,
  PAWN,
  TYPE_MASK,
  WHITE,
  WHITE_BIT,
  COLOR_MASK,
  moveFlags,
  moveFrom,
  movePromo,
  moveTo,
} from './board.ts';
import { mulberry32 } from './board.ts';
import { hashPosition } from './hash.ts';
import { baseType } from './pieces.ts';
import { type Rules, rulesFor } from './rules.ts';
import type { Position } from './types.ts';

export interface AiLevel {
  id: number;
  name: string;
  blurb: string;
  /** Maximum search depth in plies. */
  maxDepth: number;
  /** Time budget for iterative deepening; 0 = search exactly maxDepth (within FIXED_DEPTH_NODES). */
  timeMs: number;
  /** Random centipawns added to each root move's score. */
  noise: number;
  /** Chance of just playing a random legal move. */
  blunder: number;
}

export const AI_LEVELS: readonly AiLevel[] = [
  { id: 1, name: 'Rookie', blurb: 'Still learning how the pieces move', maxDepth: 1, timeMs: 0, noise: 180, blunder: 0.25 },
  { id: 2, name: 'Casual', blurb: 'Friendly, makes mistakes', maxDepth: 2, timeMs: 0, noise: 70, blunder: 0.05 },
  { id: 3, name: 'Club', blurb: 'Spots simple tactics', maxDepth: 3, timeMs: 0, noise: 20, blunder: 0 },
  { id: 4, name: 'Expert', blurb: 'A solid player', maxDepth: 6, timeMs: 1200, noise: 0, blunder: 0 },
  { id: 5, name: 'Grandmaster', blurb: 'Thinks hard. Good luck.', maxDepth: 30, timeMs: 3000, noise: 0, blunder: 0 },
];

/**
 * Work cap for the fixed-depth levels. Counted in nodes, not time, so a capped search still
 * plays the same move on every machine (the daily challenge is checked on the server).
 */
export const FIXED_DEPTH_NODES = 400_000;

export const MATE = 100000;
const INF = 1000000;
const MAX_PLY = 64;
const PHASE_WEIGHT = [0, 0, 1, 1, 2, 4, 0];

// Piece-square tables, written from White's point of view with rank 8 first.
// prettier-ignore
const PST: number[][] = [
  [],
  [ // pawn
     0,  0,  0,  0,  0,  0,  0,  0,
    50, 50, 50, 50, 50, 50, 50, 50,
    10, 10, 20, 30, 30, 20, 10, 10,
     5,  5, 10, 25, 25, 10,  5,  5,
     0,  0,  0, 20, 20,  0,  0,  0,
     5, -5,-10,  0,  0,-10, -5,  5,
     5, 10, 10,-20,-20, 10, 10,  5,
     0,  0,  0,  0,  0,  0,  0,  0,
  ],
  [ // knight
   -50,-40,-30,-30,-30,-30,-40,-50,
   -40,-20,  0,  0,  0,  0,-20,-40,
   -30,  0, 10, 15, 15, 10,  0,-30,
   -30,  5, 15, 20, 20, 15,  5,-30,
   -30,  0, 15, 20, 20, 15,  0,-30,
   -30,  5, 10, 15, 15, 10,  5,-30,
   -40,-20,  0,  5,  5,  0,-20,-40,
   -50,-40,-30,-30,-30,-30,-40,-50,
  ],
  [ // bishop
   -20,-10,-10,-10,-10,-10,-10,-20,
   -10,  0,  0,  0,  0,  0,  0,-10,
   -10,  0,  5, 10, 10,  5,  0,-10,
   -10,  5,  5, 10, 10,  5,  5,-10,
   -10,  0, 10, 10, 10, 10,  0,-10,
   -10, 10, 10, 10, 10, 10, 10,-10,
   -10,  5,  0,  0,  0,  0,  5,-10,
   -20,-10,-10,-10,-10,-10,-10,-20,
  ],
  [ // rook
     0,  0,  0,  0,  0,  0,  0,  0,
     5, 10, 10, 10, 10, 10, 10,  5,
    -5,  0,  0,  0,  0,  0,  0, -5,
    -5,  0,  0,  0,  0,  0,  0, -5,
    -5,  0,  0,  0,  0,  0,  0, -5,
    -5,  0,  0,  0,  0,  0,  0, -5,
    -5,  0,  0,  0,  0,  0,  0, -5,
     0,  0,  0,  5,  5,  0,  0,  0,
  ],
  [ // queen
   -20,-10,-10, -5, -5,-10,-10,-20,
   -10,  0,  0,  0,  0,  0,  0,-10,
   -10,  0,  5,  5,  5,  5,  0,-10,
    -5,  0,  5,  5,  5,  5,  0, -5,
     0,  0,  5,  5,  5,  5,  0, -5,
   -10,  5,  5,  5,  5,  5,  0,-10,
   -10,  0,  5,  0,  0,  0,  0,-10,
   -20,-10,-10, -5, -5,-10,-10,-20,
  ],
  [ // king, middlegame
   -30,-40,-40,-50,-50,-40,-40,-30,
   -30,-40,-40,-50,-50,-40,-40,-30,
   -30,-40,-40,-50,-50,-40,-40,-30,
   -30,-40,-40,-50,-50,-40,-40,-30,
   -20,-30,-30,-40,-40,-30,-30,-20,
   -10,-20,-20,-20,-20,-20,-20,-10,
    20, 20,  0,  0,  0,  0, 20, 20,
    20, 30, 10,  0,  0, 10, 30, 20,
  ],
];
// prettier-ignore
const KING_ENDGAME = [
   -50,-40,-30,-20,-20,-30,-40,-50,
   -30,-20,-10,  0,  0,-10,-20,-30,
   -30,-10, 20, 30, 30, 20,-10,-30,
   -30,-10, 30, 40, 40, 30,-10,-30,
   -30,-10, 30, 40, 40, 30,-10,-30,
   -30,-10, 20, 30, 30, 20,-10,-30,
   -30,-30,  0,  0,  0,  0,-30,-30,
   -50,-30,-30,-30,-30,-30,-30,-50,
];

/** Static evaluation in centipawns from the side to move's point of view. */
export function evaluate(rules: Rules, pos: Position): number {
  const b = pos.board;
  const defs = rules.defs;
  let mg = 0;
  let eg = 0;
  let phase = 0;
  for (let sq = 0; sq < 64; sq++) {
    const p = b[sq];
    if (p === 0 || !(p & COLOR_MASK)) continue;
    const type = p & TYPE_MASK;
    const white = (p & WHITE_BIT) !== 0;
    const sign = white ? 1 : -1;
    const def = defs[type]!;
    const base = def.value;
    // States of standard pieces (a wounded knight) use that piece's table.
    const pst = type <= KING ? type : (def.like ?? 0);
    if (pst) {
      const idx = white ? (7 - (sq >> 3)) * 8 + (sq & 7) : sq;
      mg += sign * (base + PST[pst][idx]);
      eg += sign * (base + (pst === KING ? KING_ENDGAME[idx] : PST[pst][idx]));
      phase += PHASE_WEIGHT[pst];
    } else {
      mg += sign * base;
      eg += sign * base;
      phase += Math.min(4, Math.round(base / 250));
    }
  }
  if (pos.hands) {
    for (const t of rules.types) {
      const v = defs[t]!.value;
      mg += (pos.hands[t] - pos.hands[32 + t]) * v;
      eg += (pos.hands[t] - pos.hands[32 + t]) * v;
    }
  }
  phase = Math.min(phase, 24);
  let score = (mg * phase + eg * (24 - phase)) / 24;
  if (rules.variant.evaluate) score += rules.variant.evaluate(pos);
  score = Math.round(score);
  return pos.turn === WHITE ? score : -score;
}

export interface SearchOptions {
  maxDepth: number;
  timeMs?: number;
  noise?: number;
  blunder?: number;
  /** Repetition keys (`h1` hashes) of earlier positions in the game, oldest first. */
  history?: number[];
  random?: () => number;
  /** Stop after this many nodes (deterministic, unlike timeMs). */
  maxNodes?: number;
}

export interface SearchResult {
  move: number;
  score: number;
  depth: number;
  nodes: number;
}

const TT_BITS = 18;
const TT_SIZE = 1 << TT_BITS;
const TT_MASK = TT_SIZE - 1;
const TT_EXACT = 1;
const TT_LOWER = 2;
const TT_UPPER = 3;

class Searcher {
  nodes = 0;
  stopped = false;
  private deadline = 0;
  /** Threat extensions only up to this ply, so chains of threats can't blow up the search. */
  private extendUntil = MAX_PLY - 8;
  private readonly killers = new Int32Array(MAX_PLY * 2);
  private readonly historyScores = new Int32Array(64 * 64);
  private readonly ttKey = new Int32Array(TT_SIZE);
  private readonly ttMove = new Int32Array(TT_SIZE);
  private readonly ttScore = new Int32Array(TT_SIZE);
  private readonly ttDepth = new Int8Array(TT_SIZE);
  private readonly ttFlag = new Int8Array(TT_SIZE);
  /** h1 hashes of every position before the current node. */
  private readonly path: number[];
  private readonly orderValue: number[] = [];
  /** Positions repeat every 2 plies unless the variant can give a side two moves in a row. */
  private readonly repStep: number;
  private readonly fifty: boolean;

  constructor(
    private readonly rules: Rules,
    history: number[],
    timeMs: number,
    private readonly maxNodes = 0,
  ) {
    this.path = history.slice();
    if (timeMs > 0) this.deadline = Date.now() + timeMs;
    for (const t of rules.types) this.orderValue[t] = rules.isRoyal[t] ? 20000 : rules.defs[t]!.value;
    this.repStep = rules.variant.nextTurn ? 1 : 2;
    this.fifty = rules.variant.fiftyMoveRule ?? true;
  }

  private tick() {
    if (++this.nodes > this.maxNodes && this.maxNodes) this.stopped = true;
    else if ((this.nodes & 1023) === 0 && this.deadline && Date.now() > this.deadline) this.stopped = true;
  }

  private setRootDepth(depth: number) {
    this.extendUntil = Math.min(MAX_PLY - 8, 2 * depth + 2);
  }

  private isRepetition(h1: number, halfmove: number): boolean {
    const path = this.path;
    const stop = Math.max(0, path.length - halfmove);
    for (let i = path.length - this.repStep; i >= stop; i -= this.repStep) if (path[i] === h1) return true;
    return false;
  }

  private scoreMoves(pos: Position, moves: number[], ttMove: number, ply: number): Int32Array {
    const scores = new Int32Array(moves.length);
    const k1 = this.killers[ply * 2];
    const k2 = this.killers[ply * 2 + 1];
    const ov = this.orderValue;
    for (let i = 0; i < moves.length; i++) {
      const m = moves[i];
      const flags = moveFlags(m);
      if (m === ttMove) scores[i] = 2_000_000;
      else if (flags & FLAG_CAPTURE) {
        const victim = pos.board[moveTo(m)] & TYPE_MASK || 1;
        const attacker = pos.board[moveFrom(m)] & TYPE_MASK;
        scores[i] = 1_000_000 + (ov[victim] ?? 100) * 10 - (ov[attacker] ?? 100);
      } else if (movePromo(m)) scores[i] = 900_000 + (ov[movePromo(m)] ?? 0);
      else if (m === k1) scores[i] = 800_000;
      else if (m === k2) scores[i] = 700_000;
      else if (flags & FLAG_DROP) scores[i] = 0;
      else scores[i] = this.historyScores[moveFrom(m) * 64 + moveTo(m)];
    }
    return scores;
  }

  private static pick(moves: number[], scores: Int32Array, i: number) {
    let best = i;
    for (let j = i + 1; j < moves.length; j++) if (scores[j] > scores[best]) best = j;
    if (best !== i) {
      const m = moves[i];
      moves[i] = moves[best];
      moves[best] = m;
      const s = scores[i];
      scores[i] = scores[best];
      scores[best] = s;
    }
  }

  /** Moves to try at a node. Variants with compulsory moves get the filtered legal list. */
  private candidates(pos: Position, capturesOnly: boolean): number[] {
    const rules = this.rules;
    if (!rules.variant.filterLegal) return rules.pseudoMoves(pos, capturesOnly);
    const legal = rules.legalMoves(pos);
    return capturesOnly ? legal.filter((m) => moveFlags(m) & FLAG_CAPTURE || movePromo(m)) : legal;
  }

  /** Mate/draw score of a finished position for the side to move, or null if not finished. */
  private terminalScore(pos: Position, ply: number): number | null {
    const t = this.rules.terminal(pos);
    if (!t) return null;
    if (t.winner === undefined) return 0;
    return t.winner === pos.turn ? MATE - ply : -MATE + ply;
  }

  private noMovesScore(pos: Position, ply: number): number {
    const s = this.rules.noMovesStatus(pos);
    if (s.winner === undefined) return 0;
    return s.winner === pos.turn ? MATE - ply : -MATE + ply;
  }

  /** Searches a child, flipping perspective unless the same side moves again. */
  private child(pos: Position, child: Position, depth: number, alpha: number, beta: number, ply: number): number {
    if (child.turn === pos.turn) return this.search(child, depth, alpha, beta, ply);
    return -this.search(child, depth, -beta, -alpha, ply);
  }

  search(pos: Position, depth: number, alpha: number, beta: number, ply: number): number {
    this.tick();
    if (this.stopped) return 0;
    const term = this.terminalScore(pos, ply);
    if (term !== null) return term;
    const { h1, h2 } = hashPosition(this.rules, pos);
    if (ply > 0 && ((this.fifty && pos.halfmove >= 100) || this.isRepetition(h1, pos.halfmove))) return 0;
    const threatened = this.rules.isThreatened(pos);
    if (threatened && ply < this.extendUntil) depth++;
    if (depth <= 0 || ply >= MAX_PLY - 1) return this.quiesce(pos, alpha, beta, ply, 0);

    const slot = h1 & TT_MASK;
    let ttMove = 0;
    if (this.ttFlag[slot] && this.ttKey[slot] === h2) {
      ttMove = this.ttMove[slot];
      if (ply > 0 && this.ttDepth[slot] >= depth) {
        let s = this.ttScore[slot];
        if (s > MATE - 1000) s -= ply;
        else if (s < -MATE + 1000) s += ply;
        const flag = this.ttFlag[slot];
        if (flag === TT_EXACT) return s;
        if (flag === TT_LOWER && s >= beta) return s;
        if (flag === TT_UPPER && s <= alpha) return s;
      }
    }

    const moves = this.candidates(pos, false);
    const scores = this.scoreMoves(pos, moves, ttMove, ply);
    const origAlpha = alpha;
    let best = -INF;
    let bestMove = 0;
    let legal = 0;
    this.path.push(h1);
    for (let i = 0; i < moves.length; i++) {
      Searcher.pick(moves, scores, i);
      const m = moves[i];
      const child = this.rules.apply(pos, m);
      if (!child) continue;
      legal++;
      let score: number;
      if (legal === 1) score = this.child(pos, child, depth - 1, alpha, beta, ply + 1);
      else {
        score = this.child(pos, child, depth - 1, alpha, alpha + 1, ply + 1);
        if (score > alpha && score < beta) score = this.child(pos, child, depth - 1, alpha, beta, ply + 1);
      }
      if (this.stopped) break;
      if (score > best) {
        best = score;
        bestMove = m;
      }
      if (score > alpha) alpha = score;
      if (alpha >= beta) {
        if (!(moveFlags(m) & (FLAG_CAPTURE | FLAG_DROP))) {
          if (this.killers[ply * 2] !== m) {
            this.killers[ply * 2 + 1] = this.killers[ply * 2];
            this.killers[ply * 2] = m;
          }
          this.historyScores[moveFrom(m) * 64 + moveTo(m)] += depth * depth;
        }
        break;
      }
    }
    this.path.pop();
    if (this.stopped) return 0;
    if (legal === 0) return this.noMovesScore(pos, ply);

    let stored = best;
    if (stored > MATE - 1000) stored += ply;
    else if (stored < -MATE + 1000) stored -= ply;
    this.ttKey[slot] = h2;
    this.ttMove[slot] = bestMove;
    this.ttScore[slot] = stored;
    this.ttDepth[slot] = depth;
    this.ttFlag[slot] = best <= origAlpha ? TT_UPPER : best >= beta ? TT_LOWER : TT_EXACT;
    return best;
  }

  private quiesce(pos: Position, alpha: number, beta: number, ply: number, qply: number): number {
    this.tick();
    if (this.stopped) return 0;
    const term = this.terminalScore(pos, ply);
    if (term !== null) return term;
    const evading = qply < 4 && ply < MAX_PLY - 1 && this.rules.isThreatened(pos);
    let best = -INF;
    if (!evading) {
      const stand = evaluate(this.rules, pos);
      if (stand >= beta || ply >= MAX_PLY - 1) return stand;
      // A side with no legal moves isn't "standing pat": it's stalemated (or whatever the
      // variant says), so don't score it as material. Past the horizon this only matters when
      // a draw would beat what the side to move has (it's behind), and stalemates need a side
      // with hardly any pieces left, so only those positions pay for the check.
      if ((qply === 0 || (stand < 0 && alpha < 0 && this.fewPieces(pos))) && !this.rules.hasLegalMove(pos)) {
        return this.noMovesScore(pos, ply);
      }
      if (stand > alpha) alpha = stand;
      best = stand;
    }
    const moves = this.candidates(pos, !evading);
    const scores = this.scoreMoves(pos, moves, 0, Math.min(ply, MAX_PLY - 1));
    let legal = 0;
    for (let i = 0; i < moves.length; i++) {
      Searcher.pick(moves, scores, i);
      const child = this.rules.apply(pos, moves[i]);
      if (!child) continue;
      legal++;
      const score =
        child.turn === pos.turn
          ? this.quiesce(child, alpha, beta, ply + 1, qply + 1)
          : -this.quiesce(child, -beta, -alpha, ply + 1, qply + 1);
      if (this.stopped) return 0;
      if (score > best) best = score;
      if (score > alpha) alpha = score;
      if (alpha >= beta) break;
    }
    if (evading && legal === 0) return this.noMovesScore(pos, ply);
    return best;
  }

  /** Whether the side to move has at most two pieces besides its king and pawns. */
  private fewPieces(pos: Position): boolean {
    const b = pos.board;
    const white = pos.turn === WHITE;
    const bit = white ? WHITE_BIT : BLACK_BIT;
    let n = 0;
    // From that side's back rank, where its pieces usually are, so the answer is quick.
    for (let i = 0; i < 64; i++) {
      const p = b[white ? i : 63 - i];
      if (!(p & bit)) continue;
      const t = p & TYPE_MASK;
      const base = baseType(t);
      if (base !== KING && base !== PAWN && ++n > 2) return false;
    }
    return true;
  }

  /** Score of a root move that wasn't searched before the node cap: its outcome if the game is over, else a static evaluation. */
  private fallbackScore(pos: Position, child: Position): number {
    const status = this.rules.terminal(child) ?? (this.rules.hasLegalMove(child) ? null : this.rules.noMovesStatus(child));
    if (status) return status.winner === undefined ? 0 : status.winner === pos.turn ? MATE - 1 : -MATE + 1;
    const e = evaluate(this.rules, child);
    return child.turn === pos.turn ? e : -e;
  }

  /**
   * Full-window score of every root move at a fixed depth (used for the noisy levels). Moves
   * not searched before the node cap fall back to a static evaluation.
   */
  scoreRoot(pos: Position, legal: number[], depth: number): number[] {
    this.setRootDepth(depth);
    const { h1 } = hashPosition(this.rules, pos);
    this.path.push(h1);
    const out = legal.map((m) => {
      const child = this.rules.apply(pos, m)!;
      const score = this.stopped ? 0 : this.child(pos, child, depth - 1, -INF, INF, 1);
      return this.stopped ? this.fallbackScore(pos, child) : score;
    });
    this.path.pop();
    return out;
  }

  /** One iterative-deepening iteration; root moves are reordered best-first. */
  rootIteration(pos: Position, rootMoves: number[], depth: number): { move: number; score: number } | null {
    this.setRootDepth(depth);
    const { h1 } = hashPosition(this.rules, pos);
    this.path.push(h1);
    let alpha = -INF;
    let bestIdx = -1;
    for (let i = 0; i < rootMoves.length; i++) {
      const child = this.rules.apply(pos, rootMoves[i])!;
      let score: number;
      if (i === 0) score = this.child(pos, child, depth - 1, -INF, INF, 1);
      else {
        score = this.child(pos, child, depth - 1, alpha, alpha + 1, 1);
        if (score > alpha && !this.stopped) score = this.child(pos, child, depth - 1, alpha, INF, 1);
      }
      if (this.stopped) break;
      if (score > alpha) {
        alpha = score;
        bestIdx = i;
      }
    }
    this.path.pop();
    if (this.stopped || bestIdx < 0) return null;
    const [m] = rootMoves.splice(bestIdx, 1);
    rootMoves.unshift(m);
    return { move: m, score: alpha };
  }
}

/** Picks a move for the side to move. Returns null if there are no legal moves. */
export function findBestMove(rules: Rules, pos: Position, opts: SearchOptions): SearchResult | null {
  const random = opts.random ?? Math.random;
  const legal = rules.legalMoves(pos);
  if (!legal.length) return null;
  if (legal.length === 1) return { move: legal[0], score: 0, depth: 0, nodes: 0 };
  if (opts.blunder && random() < opts.blunder) {
    return { move: legal[Math.floor(random() * legal.length)], score: 0, depth: 0, nodes: 0 };
  }

  const searcher = new Searcher(rules, opts.history ?? [], opts.timeMs ?? 0, opts.maxNodes ?? 0);
  const noise = opts.noise ?? 0;
  if (noise > 0) {
    const scores = searcher.scoreRoot(pos, legal, Math.max(1, opts.maxDepth));
    let best = 0;
    let bestScore = -INF;
    scores.forEach((s, i) => {
      const noisy = s + (random() * 2 - 1) * noise;
      if (noisy > bestScore) {
        bestScore = noisy;
        best = i;
      }
    });
    return { move: legal[best], score: scores[best], depth: opts.maxDepth, nodes: searcher.nodes };
  }

  const rootMoves = legal.slice();
  let result: SearchResult = { move: rootMoves[0], score: 0, depth: 0, nodes: 0 };
  const started = Date.now();
  for (let depth = 1; depth <= opts.maxDepth; depth++) {
    const iteration = searcher.rootIteration(pos, rootMoves, depth);
    if (!iteration) break;
    result = { ...iteration, depth, nodes: searcher.nodes };
    if (Math.abs(iteration.score) > MATE - 1000) break;
    if (opts.timeMs && Date.now() - started > opts.timeMs * 0.45) break;
  }
  result.nodes = searcher.nodes;
  return result;
}

/**
 * The random stream for a deterministic bot (the daily challenge): one per ply, so the same
 * position in the same game always gets the same move, in the browser and on the server.
 */
export function seededBotRandom(seed: number, ply: number): () => number {
  return mulberry32((seed ^ Math.imul(ply + 1, 0x9e3779b1)) >>> 0);
}

/** Convenience wrapper: search with a named difficulty level. */
export function findMoveForLevel(
  rules: Rules,
  pos: Position,
  level: AiLevel,
  history: number[] = [],
  random: () => number = Math.random,
): SearchResult | null {
  const opts = {
    maxDepth: level.maxDepth,
    timeMs: level.timeMs,
    noise: level.noise,
    blunder: level.blunder,
    history,
    random,
    maxNodes: level.timeMs > 0 ? 0 : FIXED_DEPTH_NODES,
  };
  const custom = rules.variant.botMove;
  if (custom) {
    const move = custom(pos, {
      level: level.id,
      random,
      history,
      search: (variant, p) => findBestMove(rulesFor(variant), p, opts)?.move ?? null,
    });
    if (move !== null) return { move, score: 0, depth: 0, nodes: 0 };
  }
  return findBestMove(rules, pos, opts);
}
